import { css } from '@emotion/react'
import { Cell, Flow, Grid } from 'bold-ui'
import { CboSelectField, SimNaoEnum, SimNaoRadioGroupField } from 'components/form'
import { RecordStringSelectField } from 'components/form/core/RecordStringSelectField/RecordStringSelectField'
import { EscolaridadeSelectField } from 'components/form/field/select/EscolaridadeSelectField'
import theme from 'config/theme'
import { differenceInYears } from 'date-fns'
import { MetaPath } from 'util/metaPath'
import { createFormSectionStyles } from 'view/cidadao/utils-cidadao'

import { FormRowSimNao } from '../../../../components/form/field/FormRowSimNao'
import { CidadaoTourIdEnum } from '../tour-cidadao'
import {
  criancas0a9anosComQuemFicaRecord,
  deficienciaRecord,
  InformacoesSociodemograficasFormModel,
  povoComunidadeTradicionalRecord,
  situacaoMercadoTrabalhoRecord,
  tempoAtendimentoSocioeducativoRecord,
  tipoAtendimentoSocioeducativoRecord,
} from '../types/model-cidadao'
import { FormSectionAccordionWithErrors } from './FormSectionAccordionWithErrors'

interface InformacoesSociodemograficasFormSectionProps {
  meta: MetaPath<InformacoesSociodemograficasFormModel>
  values: InformacoesSociodemograficasFormModel
  dataNascimento: string
  isAtendimentoSocioeducativoEnabled: boolean
}

export const InformacoesSociodemograficasFormSection = ({
  meta,
  values,
  dataNascimento,
  isAtendimentoSocioeducativoEnabled,
}: InformacoesSociodemograficasFormSectionProps) => {
  const styles = createFormSectionStyles(theme)

  const idade = dataNascimento ? differenceInYears(new Date(), new Date(dataNascimento)) : 0
  const isIdadeAtendimentoSocioeducativo = idade >= 12 && idade <= 21
  const isAtendimentoSocioeducativoVisible = isAtendimentoSocioeducativoEnabled && isIdadeAtendimentoSocioeducativo
  const isAdolescenteAtendimentoSocioeducativo = values?.isAtendimentoSocioeducativo === SimNaoEnum.SIM

  return (
    <FormSectionAccordionWithErrors
      name={meta.openedAccordion}
      title='Informações sociodemográficas'
      uuid='informacoesSociodemograficas'
      buttonId={CidadaoTourIdEnum.FORM_SOCIODEMOGRAFICAS}
      textStyle={css`
        font-size: 1rem;
      `}
      panelStyle={styles.rowsContainer}
    >
      <Flow direction='vertical'>
        <Grid>
          <Cell size={6}>
            <EscolaridadeSelectField name={meta.escolaridade} title='Escolaridade' label='Escolaridade' />
          </Cell>
          <Cell size={6}>
            <SimNaoRadioGroupField
              name={meta.frequentaEscolaOuCreche}
              label='Frequenta escola ou creche?'
              globalClearable
            />
          </Cell>
          <Cell size={6}>
            <RecordStringSelectField
              name={meta.criancas0a9anosComQuemFica}
              record={criancas0a9anosComQuemFicaRecord}
              label='Com quem fica a criança? (Menores de 10 anos)'
              title='Com quem fica a criança? (Menores de 10 anos)'
              multiple
              disabled={idade > 9}
            />
          </Cell>
          <Cell size={6} />
          <Cell size={6}>
            <RecordStringSelectField
              name={meta.situacaoMercadoTrabalho}
              record={situacaoMercadoTrabalhoRecord}
              title='Situação no mercado de trabalho'
              label='Situação no mercado de trabalho'
            />
          </Cell>
          <Cell size={6}>
            <CboSelectField name={meta.ocupacao} label='Ocupação' mostrarIndisponivelLotacao />
          </Cell>
        </Grid>
      </Flow>

      {isAtendimentoSocioeducativoVisible && (
        <FormRowSimNao
          name={meta.isAtendimentoSocioeducativo}
          title='Adolescente em atendimento socioeducativo?'
          clearable
        >
          <Flow direction='vertical' gap={0.5} justifyItems='stretch' style={styles.fixedWidthInputContainer}>
            <RecordStringSelectField
              name={meta.tipoAtendimentoSocioeducativo}
              record={tipoAtendimentoSocioeducativoRecord}
              label='Tipo de atendimento'
              title='Tipo de atendimento'
              disabled={!isAdolescenteAtendimentoSocioeducativo}
              required={isAdolescenteAtendimentoSocioeducativo}
            />
            <RecordStringSelectField
              name={meta.tempoAtendimentoSocioeducativo}
              record={tempoAtendimentoSocioeducativoRecord}
              label='Tempo de atendimento socioeducativo'
              title='Tempo de atendimento socioeducativo'
              disabled={!isAdolescenteAtendimentoSocioeducativo}
              required={isAdolescenteAtendimentoSocioeducativo}
            />
          </Flow>
        </FormRowSimNao>
      )}

      <FormRowSimNao name={meta.possuiPlanoSaudePrivado} title='Possui plano de saúde privado?' clearable />
      <FormRowSimNao name={meta.frequentaCuidadorTradicional} title='Frequenta cuidador tradicional?' clearable />
      <FormRowSimNao
        name={meta.participaAlgumGrupoComunitario}
        title='Participa de algum grupo comunitário?'
        clearable
      />

      <FormRowSimNao
        name={meta.isMembroComunidadeTradicional}
        title='É membro de Povo ou Comunidade Tradicional ou População de Campo, Floresta e Águas?'
        clearable
      >
        <div css={styles.fixedWidthInputContainer}>
          <RecordStringSelectField
            name={meta.povoComunidadeTradicional}
            record={povoComunidadeTradicionalRecord}
            label='Indique qual'
            title='Indique qual'
            disabled={values?.isMembroComunidadeTradicional !== SimNaoEnum.SIM}
          />
        </div>
      </FormRowSimNao>

      <FormRowSimNao name={meta.possuiDeficiencia} title='Possui alguma deficiência?' clearable>
        <div css={styles.fixedWidthInputContainer}>
          <RecordStringSelectField
            name={meta.deficiencias}
            record={deficienciaRecord}
            label='Indique qual(is)'
            title='Indique qual(is)'
            disabled={values?.possuiDeficiencia !== SimNaoEnum.SIM}
            multiple
            required={values?.possuiDeficiencia === SimNaoEnum.SIM}
          />
        </div>
      </FormRowSimNao>
    </FormSectionAccordionWithErrors>
  )
}
